import { type ReactElement, useState, useRef, useEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Receipt, Users, Package, BookOpen, Landmark, Banknote, FileSignature, CreditCard, Gift, Settings as SettingsIcon, LogOut, Building2, Plus, ChevronsUpDown, Check, Wallet, TrendingUp, FileCheck } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';
import { useCompany } from './CompanyProvider';
import AvvGate from './AvvGate';
import MobileTabBar from './MobileTabBar';
import { Logo } from '../components/Logo';

const ROLE_LABEL: Record<string, string> = {
  owner: 'Inhaber', admin: 'Admin', accountant: 'Steuerberater', member: 'Mitglied',
};

/**
 * Mandanten-Umschalter in der Sidebar. Erscheint nur, wenn der Nutzer Mitglied
 * mehrerer Firmen ist (z.B. Admin-Einsicht in mehrere Mandanten). Beim Wechsel
 * wird die aktive companyId gesetzt — alle Ansichten laden mandanten-scoped neu.
 */
function CompanySwitcher(): ReactElement | null {
  const { companies, active, setActive } = useCompany();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Bei nur einer Firma kein Umschalter (kein UI-Ballast).
  if (companies.length <= 1) return null;

  return (
    <div ref={ref} className="relative z-[60] mb-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-left text-sm transition hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
      >
        <Building2 className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
        <span className="flex-1 truncate font-medium text-gray-900 dark:text-gray-100">
          {active?.name ?? 'Firma wählen'}
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-20 mb-1 max-h-80 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {companies.map((c) => (
            <button
              key={c.id}
              onClick={() => { setActive(c.id); setOpen(false); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-blue-50 dark:hover:bg-gray-700"
            >
              <Check className={`h-4 w-4 shrink-0 ${c.id === active?.id ? 'text-blue-600' : 'text-transparent'}`} />
              <span className="flex-1 truncate text-gray-900 dark:text-gray-100">{c.name}</span>
              <span className="shrink-0 text-xs text-gray-400">{ROLE_LABEL[c.role] ?? c.role}</span>
            </button>
          ))}
          {/* Weitere Firma anlegen. Das Onboarding kann das laengst — es gab
              bisher nur keinen Weg dorthin, wenn man schon eine Firma hat. */}
          <Link
            to="/onboarding?weitere=1"
            onClick={() => setOpen(false)}
            className="mt-1 flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2 text-left text-sm text-blue-600 transition hover:bg-blue-50 dark:border-gray-700 dark:text-blue-400 dark:hover:bg-gray-700"
          >
            <Plus className="h-4 w-4 shrink-0" />
            <span className="flex-1">Weitere Firma anlegen</span>
          </Link>
        </div>
      )}
    </div>
  );
}

const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter', plus: 'Plus', professional: 'Professional', business: 'Business',
};

/**
 * Rahmen des eingeloggten Bereichs: schlanke Sidebar + Inhalt.
 * Die Buchhaltungs-Module folgen — hier stehen vorerst Platzhalter.
 */
export default function AppLayout(): ReactElement {
  const { user, logout } = useAuth();
  const { active } = useCompany();
  const navigate = useNavigate();
  const plan = active?.plan ?? 'starter';
  const isStarter = plan === 'starter';

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  const navItems = [
    { to: '/app', label: 'Übersicht', icon: LayoutDashboard, end: true },
    { to: '/app/rechnungen', label: 'Rechnungen', icon: FileText, end: false },
    { to: '/app/kunden', label: 'Kontakte', icon: Users, end: false },
    { to: '/app/produkte', label: 'Produkte', icon: Package, end: false },
    { to: '/app/belege', label: 'Belege', icon: Receipt, end: false },
    { to: '/app/kassenbuch', label: 'Kassenbuch', icon: Wallet, end: false },
    { to: '/app/nachweise', label: 'Nachweise', icon: FileCheck, end: false },
    { to: '/app/buchhaltung', label: 'Buchhaltung', icon: BookOpen, end: false },
    { to: '/app/kennzahlen', label: 'Kennzahlen', icon: TrendingUp, end: false },
    { to: '/app/steuern', label: 'Steuern', icon: Landmark, end: false },
    { to: '/app/banking', label: 'Banking & Mahnwesen', icon: Banknote, end: false },
    { to: '/app/formulare', label: 'Formulare', icon: FileSignature, end: false },
    { to: '/app/einstellungen', label: 'Einstellungen', icon: SettingsIcon, end: true },
    { to: '/app/einstellungen/tarif', label: 'Tarif', icon: CreditCard, end: false },
    { to: '/app/partner', label: 'Partner', icon: Gift, end: false },
  ];

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* z-[60]: liegt ueber dem AVV-Gate-Overlay (z-50). Sonst sperrt eine Firma
          ohne akzeptierten AVV die Sidebar mit — Mandantenwechsel und Abmelden
          waeren dann nicht mehr erreichbar. */}
      <aside className="relative z-[60] hidden w-64 flex-col border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 md:flex">
        <div className="p-6">
          <Link to="/app" aria-label="Saldify" className="text-gray-900 dark:text-white">
            <Logo className="h-8 w-auto" />
          </Link>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {navItems.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-blue-50 hover:text-blue-700 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-blue-300"
            >
              <Icon className="h-5 w-5" />
              {label}
            </Link>
          ))}
        </nav>
        <div className="border-t border-gray-200 p-4 dark:border-gray-700">
          <CompanySwitcher />
          <div className="mb-2 px-1 text-sm text-gray-600 dark:text-gray-400">
            {/* Zeile 1: Name + Tarif-Badge nebeneinander */}
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100">{user?.name ?? 'Angemeldet'}</span>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  isStarter ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                }`}
              >
                <CreditCard className="h-3 w-3" />
                {PLAN_LABEL[plan] ?? plan}
              </span>
            </div>
            <div className="truncate text-xs">{user?.email}</div>
            {/* Zeile darunter: Upgrade-/Verwalten-Link */}
            <Link
              to="/app/einstellungen/tarif"
              className={`mt-1 inline-block text-xs font-medium hover:underline ${isStarter ? 'text-amber-700' : 'text-blue-600'}`}
            >
              {isStarter ? 'Upgrade →' : 'Tarif verwalten →'}
            </Link>
          </div>
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          >
            <LogOut className="h-4 w-4" />
            Abmelden
          </button>
        </div>
      </aside>
      {/* Inhalt: am Desktop 8er-Padding, mobil schlanker + unten Platz für die Tab-Bar.
          min-w-0 + overflow-x-hidden: ein flex-1-Kind waechst sonst auf die
          intrinsische Inhaltsbreite -> horizontaler Scroll der ganzen Seite auf
          Mobile. Das verhindert app-weit jegliches Seiten-Querscrollen. */}
      <main className="min-w-0 flex-1 overflow-x-hidden p-4 pb-24 md:p-8 md:pb-8">
        {/* AVV-Gate: blockiert die App, bis der Auftragsverarbeitungsvertrag der
            aktuellen Fassung akzeptiert ist (Art. 28 DSGVO). */}
        <AvvGate><Outlet /></AvvGate>
      </main>
      <MobileTabBar />
    </div>
  );
}
